// PartnerDetail › Points (docs/PARTNER_API.md § 4.6): how the partner's cardholders earn points on purchases (earn
// rules: the best one in force applies, never added up), its points pool (deposits and corrections approved by a
// second system administrator), what its points add up to, and its points settings. QOR's to set; partners only see.
import { shortDate } from '@qor/web-shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { fundsApi, pointsApi, type Approval, type EarnRule, type SettingValue } from '../api';
import { ApprovalsTable, approvalKind } from '../components/Approvals';
import { useStaff } from '../components/RequireStaff';
import { SettingsForm } from '../components/SettingsForm';
import { confirmed } from '../components/ui';

const n0 = (n: number) => n.toLocaleString('en-US');
const day = (iso: string) => `${iso.slice(0, 10)}T00:00:00Z`;

export function PartnerPointsPanel({
  programId,
  after,
}: {
  programId: string;
  after: (ok: boolean, message: string) => void;
}) {
  const client = useQueryClient();
  const { user } = useStaff();
  const editable = user.role === 'SystemAdmin';
  const [busy, setBusy] = useState(false);
  const [page, setPage] = useState(1);
  const summary = useQuery({ queryKey: ['points-summary', programId], queryFn: () => pointsApi.summary(programId) });
  const rules = useQuery({ queryKey: ['points-rules', programId], queryFn: () => pointsApi.rules(programId) });
  const pending = useQuery({ queryKey: ['points-pending', programId], queryFn: () => pointsApi.pendingFor(programId) });
  const entries = useQuery({
    queryKey: ['points-pool', programId, page],
    queryFn: () => pointsApi.poolEntries(programId, page),
  });
  const settings = useQuery({ queryKey: ['points-settings', programId], queryFn: () => fundsApi.settings(programId) });
  const [rule, setRule] = useState({
    name: '',
    pointsPerUnit: '',
    minAmount: '',
    maxPoints: '',
    validFrom: '',
    validTo: '',
  });
  const [pool, setPool] = useState({
    kind: 'deposit' as 'deposit' | 'adjustment',
    points: '',
    reference: '',
    memo: '',
  });

  async function done(ok: boolean, message: string) {
    setBusy(false);
    after(ok, message);
    await Promise.all(
      ['points-summary', 'points-rules', 'points-pending', 'points-pool', 'points-settings', 'approvals'].map((k) =>
        client.invalidateQueries({ queryKey: [k] }),
      ),
    );
  }

  async function addRule(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    const r = await pointsApi.addRule(programId, {
      name: rule.name.trim(),
      pointsPerUnit: rule.pointsPerUnit.trim(),
      ...(rule.minAmount.trim() && { minAmount: rule.minAmount.trim() }),
      ...(rule.maxPoints.trim() && { maxPointsPerTransaction: Number(rule.maxPoints) }),
      ...(rule.validFrom && { validFrom: day(rule.validFrom) }),
      ...(rule.validTo && { validTo: day(rule.validTo) }),
    });
    if (r.ok) setRule({ name: '', pointsPerUnit: '', minAmount: '', maxPoints: '', validFrom: '', validTo: '' });
    await done(r.ok, r.ok ? `Rule "${r.data.name}" added: it applies to purchases from its start.` : r.error);
  }

  async function toggle(r: EarnRule) {
    if (r.isActive && !confirmed(`Stop "${r.name}"? Purchases from now on no longer earn by it.`)) return;
    setBusy(true);
    const res = await pointsApi.changeRule(programId, r.id, { isActive: !r.isActive });
    await done(res.ok, res.ok ? (r.isActive ? 'Rule stopped.' : 'Rule switched on again.') : res.error);
  }

  async function askPool(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    const r = await pointsApi.requestPool(programId, {
      kind: pool.kind,
      points: Number(pool.points),
      ...(pool.reference.trim() && { reference: pool.reference.trim() }),
      ...(pool.memo.trim() && { memo: pool.memo.trim() }),
    });
    if (r.ok) setPool({ ...pool, points: '', reference: '', memo: '' });
    await done(
      r.ok,
      r.ok ? `${approvalKind(r.data.kind)} asked: a second system administrator must approve it.` : r.error,
    );
  }

  async function decide(a: Approval, approve: boolean) {
    if (approve && !confirmed(`Approve: ${approvalKind(a.kind)}? It is done now.`)) return;
    const note = approve ? null : window.prompt('Why (optional)?');
    if (!approve && note === null) return;
    setBusy(true);
    const r = approve ? await fundsApi.approve(a.id) : await fundsApi.reject(a.id, note?.trim() || null);
    await done(r.ok, r.ok ? (approve ? 'Approved and done.' : 'Turned down.') : r.error);
  }

  async function saveSettings(change: { set: Record<string, SettingValue>; clear: string[] }) {
    setBusy(true);
    const r = await fundsApi.changeSettings(programId, change);
    await done(r.ok, r.ok ? 'Points settings saved.' : r.error);
    return r.ok ? {} : r.problems;
  }

  const s = summary.data;
  return (
    <>
      <div className="qc-panel">
        <div className="qc-panel-title">
          <h3>Points</h3>
          <span>
            {s?.earnEnabled
              ? 'Cardholders earn points on posted purchases by the rules below; refunds take them back (never below zero).'
              : 'Cardholders earn no points on purchases yet (switch it on in the settings below).'}
          </span>
        </div>
        {s && (
          <dl className="qc-detail-dl">
            <div>
              <dt>Earned (net of refunds)</dt>
              <dd>{n0(s.totals.earned)}</dd>
            </div>
            <div>
              <dt>Given</dt>
              <dd>{n0(s.totals.allocated)}</dd>
            </div>
            <div>
              <dt>Turned into money</dt>
              <dd>{n0(s.totals.redeemed)}</dd>
            </div>
            <div>
              <dt>Held by cardholders now</dt>
              <dd>
                <strong>{n0(s.totals.outstanding)}</strong>
              </dd>
            </div>
            <div>
              <dt>Pool</dt>
              <dd>{s.poolEnabled ? `${n0(s.pool.balance)} points left` : 'Not used (points are not limited)'}</dd>
            </div>
          </dl>
        )}
      </div>

      <div className="qc-panel" style={{ marginTop: 20 }}>
        <div className="qc-panel-title">
          <h3>Earn rules</h3>
          <span>For each purchase, the rule in force giving the most points applies. Changes count from now on.</span>
        </div>
        {!rules.data || rules.data.length === 0 ? (
          <p className="qc-empty">No rule yet: purchases earn nothing until one is added.</p>
        ) : (
          <table className="qc-plain-table">
            <thead>
              <tr>
                <th>Rule</th>
                <th>Points per 1.00</th>
                <th>Conditions</th>
                <th>When</th>
                <th>State</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rules.data.map((r) => (
                <tr key={r.id}>
                  <td>{r.name}</td>
                  <td>{r.pointsPerUnit}</td>
                  <td>
                    {[
                      r.minAmount && `from ${r.minAmount}`,
                      r.maxPointsPerTransaction !== null && `at most ${n0(r.maxPointsPerTransaction)} a purchase`,
                    ]
                      .filter(Boolean)
                      .join(' · ') || '—'}
                  </td>
                  <td>
                    {shortDate(r.validFrom)} → {r.validTo ? shortDate(r.validTo) : 'no end'}
                  </td>
                  <td>
                    <span className={`qc-badge ${r.inForce ? 'qc-badge-success' : 'qc-badge-muted'}`}>
                      {r.inForce ? 'In force' : r.isActive ? 'Not in force now' : 'Stopped'}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {editable && (
                      <button type="button" className="qc-button-ghost" disabled={busy} onClick={() => void toggle(r)}>
                        {r.isActive ? 'Stop' : 'Switch on'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {editable && (
          <form onSubmit={(e) => void addRule(e)} className="qc-account-field" style={{ marginTop: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 18 }}>
              <label>
                <span>Rule name</span>
                <input
                  type="text"
                  required
                  placeholder="Base, October double…"
                  value={rule.name}
                  onChange={(e) => setRule({ ...rule, name: e.target.value })}
                />
              </label>
              <label>
                <span>Points per 1.00 spent</span>
                <input
                  type="text"
                  inputMode="decimal"
                  required
                  placeholder="1 or 2.5"
                  value={rule.pointsPerUnit}
                  onChange={(e) => setRule({ ...rule, pointsPerUnit: e.target.value })}
                />
              </label>
              <label>
                <span>Minimum purchase (optional)</span>
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={rule.minAmount}
                  onChange={(e) => setRule({ ...rule, minAmount: e.target.value })}
                />
              </label>
              <label>
                <span>Most points a purchase (optional)</span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={rule.maxPoints}
                  onChange={(e) => setRule({ ...rule, maxPoints: e.target.value })}
                />
              </label>
              <label>
                <span>Starts (empty: now)</span>
                <input
                  type="date"
                  value={rule.validFrom}
                  onChange={(e) => setRule({ ...rule, validFrom: e.target.value })}
                />
              </label>
              <label>
                <span>Ends (optional)</span>
                <input
                  type="date"
                  value={rule.validTo}
                  onChange={(e) => setRule({ ...rule, validTo: e.target.value })}
                />
              </label>
            </div>
            <div>
              <button type="submit" className="qor-button qor-button-primary" disabled={busy}>
                Add rule
              </button>
            </div>
          </form>
        )}
      </div>

      <div className="qc-panel" style={{ marginTop: 20 }}>
        <div className="qc-panel-title">
          <h3>Points pool</h3>
          <span>
            Points the partner bought for its cardholders. When the pool is on, every point earned or given comes out of
            it.
          </span>
        </div>
        {editable && (
          <form onSubmit={(e) => void askPool(e)} className="qc-account-field">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 18 }}>
              <label>
                <span>What</span>
                <select
                  value={pool.kind}
                  onChange={(e) => setPool({ ...pool, kind: e.target.value as 'deposit' | 'adjustment' })}
                >
                  <option value="deposit">Deposit (points bought)</option>
                  <option value="adjustment">Correction (+ or −)</option>
                </select>
              </label>
              <label>
                <span>Points</span>
                <input
                  type="text"
                  inputMode="numeric"
                  required
                  value={pool.points}
                  onChange={(e) => setPool({ ...pool, points: e.target.value })}
                />
              </label>
              <label>
                <span>Reference</span>
                <input
                  type="text"
                  value={pool.reference}
                  onChange={(e) => setPool({ ...pool, reference: e.target.value })}
                />
              </label>
            </div>
            <label>
              <span>Note{pool.kind === 'adjustment' ? ' (required: why)' : ''}</span>
              <input
                type="text"
                required={pool.kind === 'adjustment'}
                value={pool.memo}
                onChange={(e) => setPool({ ...pool, memo: e.target.value })}
              />
            </label>
            <div>
              <button type="submit" className="qor-button qor-button-primary" disabled={busy}>
                Ask for approval
              </button>
            </div>
          </form>
        )}
        <h4 style={{ margin: '18px 0 8px' }}>Waiting for approval</h4>
        <ApprovalsTable items={pending.data ?? []} busy={busy} onDecide={(a, ok) => void decide(a, ok)} />
        <h4 style={{ margin: '18px 0 8px' }}>Pool movements</h4>
        {!entries.data || entries.data.items.length === 0 ? (
          <p className="qc-empty">No movements yet.</p>
        ) : (
          <>
            <table className="qc-plain-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>What</th>
                  <th>Points</th>
                  <th>Left</th>
                  <th>Reference / note</th>
                </tr>
              </thead>
              <tbody>
                {entries.data.items.map((e) => (
                  <tr key={e.id}>
                    <td>{shortDate(e.createdAt)}</td>
                    <td>{e.kind.replace(/_/g, ' ')}</td>
                    <td>
                      {e.points > 0 ? '+' : ''}
                      {n0(e.points)}
                    </td>
                    <td>{n0(e.balanceAfter)}</td>
                    <td>{[e.reference, e.memo].filter(Boolean).join(' · ') || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="qc-pagination">
              <span>
                Page {entries.data.page} · {entries.data.total} total
              </span>
              <div className="qc-pagination-links">
                {page > 1 && (
                  <button type="button" className="qc-button-ghost" onClick={() => setPage(page - 1)}>
                    Previous
                  </button>
                )}
                {page * entries.data.pageSize < entries.data.total && (
                  <button type="button" className="qc-button-ghost" onClick={() => setPage(page + 1)}>
                    Next
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <div className="qc-panel" style={{ marginTop: 20 }}>
        <div className="qc-panel-title">
          <h3>Points settings</h3>
          <span>This partner&apos;s own values; empty fields follow the platform&apos;s.</span>
        </div>
        {settings.data && (
          <SettingsForm
            key={settings.dataUpdatedAt}
            rows={settings.data.filter((r) => r.group === 'points')}
            editable={editable}
            busy={busy}
            onSave={saveSettings}
          />
        )}
      </div>
    </>
  );
}
